import React, { useState, useRef, useEffect } from 'react';
import { updateMarkup, deleteMarkup } from '../utils/api';
import { useStore } from '../store/useStore';
import toast from 'react-hot-toast';
import FilePickerModal from './FilePickerModal';
import SheetPickerModal from './SheetPickerModal';
import styles from './MarkupPopup.module.css';

const PRESET_COLORS = [
  '#e8a020', '#4a9eff', '#3ecf8e', '#ff5c5c',
  '#9b8aff', '#f5c842', '#ff9040', '#ffffff',
  '#808080', '#000000',
];

const TYPE_LABELS = {
  arrow: 'Arrow', cloud: 'Cloud', rect: 'Rectangle', line: 'Line',
  text: 'Text', freehand: 'Freehand', measure: 'Measure',
  'measure-length': 'Length',
  'measure-area': 'Area',
  'measure-volume': 'Volume',
};

function kindLabel(markup) {
  const k = markup.data?.kind;
  if (!k) return '';
  const map = {
    line: 'line', polyline: 'polyline', freeline: 'freeline',
    rect: 'rect', polygon: 'polygon', freeform: 'freeform',
  };
  return map[k] ? ` · ${map[k]}` : '';
}

export default function MarkupPopup({ markup, pan, zoom, containerRef, onClose, onUpdated, onDeleted }) {
  const { role } = useStore();

  const [localStroke,      setLocalStroke]      = useState(markup.color        || '#e8a020');
  const [localFill,        setLocalFill]         = useState(markup.fill_color   || 'transparent');
  const [localFillOpacity, setLocalFillOpacity]  = useState(markup.fill_opacity ?? 1);
  const [localStrokeWidth, setLocalStrokeWidth]  = useState(markup.stroke_width ?? 2);
  const [localArrowSize,   setLocalArrowSize]    = useState(markup.data?.arrowSize ?? 10);
  const [localFontSize,    setLocalFontSize]     = useState(markup.data?.fontSize  ?? 14);
  const [note,             setNote]              = useState(markup.note         || '');
  const [editingColor,     setEditingColor]      = useState(null); // 'stroke' | 'fill' | null
  const [saving,           setSaving]            = useState(false);
  const [pickingFile,      setPickingFile]       = useState(false);
  const [pickingSheet,     setPickingSheet]      = useState(false);

  // Volume-markup depth (feet stored canonically; UI toggles ft/in view).
  const initialDepthFt = Number(markup.data?.depthFt);
  const [depthUnit, setDepthUnit] = useState('ft');
  const [depthInput, setDepthInput] = useState(() => (
    Number.isFinite(initialDepthFt) && initialDepthFt > 0 ? String(initialDepthFt) : '1'
  ));

  // Publish toggle: admin / pm / super only. Field draws private personal
  // markups that nobody else sees, so publishing isn't theirs to do —
  // they'd be flipping a flag that has no effect for them.
  const canPublish = role === 'admin' || role === 'pm' || role === 'super';
  const canDelete  = role === 'admin' || role === 'pm';
  // Any signed-in role can edit a markup they can see (server filter
  // prevents field users from seeing others' markups in the first place).
  const canEdit    = !!role;

  // Position calculation
  const bbox = getMarkupBBox(markup);
  const containerW = containerRef.current?.offsetWidth  || 800;
  const containerH = containerRef.current?.offsetHeight || 600;
  const POPUP_W = 226;
  // POPUP_H is the screen-space reservation for the popup so we can
  // clamp it inside the viewer container; it grew when the LINKED SHEET
  // row was added alongside LINKED FILE.
  const POPUP_H = 410;

  let left = pan.x + (bbox.x + bbox.w) * zoom + 14;
  let top  = pan.y + bbox.y * zoom;

  if (left + POPUP_W > containerW - 8) {
    left = pan.x + bbox.x * zoom - POPUP_W - 14;
  }
  left = Math.max(8, left);
  top  = Math.max(8, Math.min(top, containerH - POPUP_H - 8));

  // Close on outside click
  const popupRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      function onDown(e) {
        if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
      }
      document.addEventListener('mousedown', onDown);
      return () => document.removeEventListener('mousedown', onDown);
    }, 120);
    return () => clearTimeout(t);
  }, [onClose]);

  async function patch(data) {
    setSaving(true);
    try {
      const updated = await updateMarkup(markup.id, data);
      onUpdated(updated);
    } catch (err) {
      toast.error('Failed to update markup');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteMarkup(markup.id);
      onDeleted(markup.id);
      onClose();
    } catch (err) {
      toast.error('Failed to delete markup');
      setSaving(false);
    }
  }

  const fillIsNone = !localFill || localFill === 'transparent';
  const isFillEdit   = editingColor === 'fill';
  const isStrokeEdit = editingColor === 'stroke';

  return (
    <div ref={popupRef} className={styles.popup} style={{ left, top }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.typeLabel}>{(TYPE_LABELS[markup.type] || markup.type) + kindLabel(markup)}</span>
        {markup.published && <span className={styles.publishedBadge}>PUBLISHED</span>}
        <button className={styles.closeBtn} onClick={onClose}>×</button>
      </div>

      {canEdit && (
        <>
          {/* Colors */}
          <div className={styles.row}>
            <span className={styles.rowLabel}>Fill</span>
            <button
              className={`${styles.colorChip} ${isFillEdit ? styles.chipEditing : ''} ${fillIsNone ? styles.chipNone : ''}`}
              style={fillIsNone ? {} : { background: localFill }}
              onClick={() => setEditingColor(isFillEdit ? null : 'fill')}
              title="Edit fill color"
            />
            <span className={styles.rowLabel} style={{ marginLeft: 8 }}>Border</span>
            <button
              className={`${styles.colorChip} ${isStrokeEdit ? styles.chipEditing : ''}`}
              style={{ background: localStroke }}
              onClick={() => setEditingColor(isStrokeEdit ? null : 'stroke')}
              title="Edit stroke color"
            />
          </div>

          {/* Color picker strip */}
          {editingColor && (
            <div className={styles.colorStrip}>
              {isFillEdit && (
                <button
                  className={`${styles.stripSwatch} ${styles.noneSwatchBtn} ${fillIsNone ? styles.stripActive : ''}`}
                  onClick={() => { setLocalFill('transparent'); patch({ fill_color: null }); setEditingColor(null); }}
                  title="No fill"
                />
              )}
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  className={`${styles.stripSwatch} ${(isFillEdit ? localFill : localStroke) === c ? styles.stripActive : ''}`}
                  style={{ background: c }}
                  onClick={() => {
                    if (isFillEdit) { setLocalFill(c); patch({ fill_color: c }); }
                    else { setLocalStroke(c); patch({ color: c }); }
                    setEditingColor(null);
                  }}
                />
              ))}
              <input
                type="color"
                value={(isFillEdit ? localFill : localStroke) === 'transparent' ? '#ffffff' : ((isFillEdit ? localFill : localStroke) || '#e8a020')}
                onChange={(e) => { isFillEdit ? setLocalFill(e.target.value) : setLocalStroke(e.target.value); }}
                onBlur={(e) => {
                  isFillEdit ? patch({ fill_color: e.target.value }) : patch({ color: e.target.value });
                  setEditingColor(null);
                }}
                className={styles.customColorInput}
              />
            </div>
          )}

          {/* Fill opacity — only shown when a fill color is active */}
          {!fillIsNone && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>Opacity</span>
              <input
                type="range" min="0" max="1" step="0.05"
                value={localFillOpacity}
                className={styles.slider}
                onChange={(e) => {
                  const v = +e.target.value;
                  setLocalFillOpacity(v);
                  onUpdated({ ...markup, fill_opacity: v });
                }}
                onMouseUp={(e) => patch({ fill_opacity: +e.target.value })}
              />
              <span className={styles.sliderVal}>{Math.round(localFillOpacity * 100)}%</span>
            </div>
          )}

          {/* Stroke width */}
          <div className={styles.row}>
            <span className={styles.rowLabel}>Width</span>
            <input
              type="range" min="0.5" max="12" step="0.5"
              value={localStrokeWidth}
              className={styles.slider}
              onChange={(e) => {
                const v = +e.target.value;
                setLocalStrokeWidth(v);
                onUpdated({ ...markup, stroke_width: v });
              }}
              onMouseUp={(e) => patch({ stroke_width: +e.target.value })}
            />
            <span className={styles.sliderVal}>{localStrokeWidth}px</span>
          </div>

          {/* Volume depth — affects computed cu ft / cu yd label */}
          {markup.type === 'measure-volume' && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>Depth</span>
              <input
                type="number"
                min="0"
                step={depthUnit === 'in' ? '0.25' : '0.1'}
                value={depthInput}
                className={styles.depthInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setDepthInput(v);
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) {
                    const newDepthFt = depthUnit === 'in' ? n / 12 : n;
                    onUpdated({ ...markup, data: { ...markup.data, depthFt: newDepthFt } });
                  }
                }}
                onBlur={() => {
                  const n = Number(depthInput);
                  if (!Number.isFinite(n) || n <= 0) {
                    // Revert to last saved value.
                    const savedFt = Number(markup.data?.depthFt);
                    setDepthInput(
                      Number.isFinite(savedFt) && savedFt > 0
                        ? String(depthUnit === 'in' ? savedFt * 12 : savedFt)
                        : '1'
                    );
                    return;
                  }
                  const newDepthFt = depthUnit === 'in' ? n / 12 : n;
                  if (newDepthFt !== Number(markup.data?.depthFt)) {
                    patch({ data: { ...markup.data, depthFt: newDepthFt } });
                  }
                }}
              />
              <select
                value={depthUnit}
                className={styles.depthUnitSelect}
                onChange={(e) => {
                  const newUnit = e.target.value;
                  // Convert displayed value so stored depthFt stays put.
                  const n = Number(depthInput);
                  if (Number.isFinite(n) && n > 0) {
                    if (newUnit === 'in' && depthUnit === 'ft') {
                      setDepthInput(String(+(n * 12).toFixed(2)));
                    } else if (newUnit === 'ft' && depthUnit === 'in') {
                      setDepthInput(String(+(n / 12).toFixed(4)));
                    }
                  }
                  setDepthUnit(newUnit);
                }}
              >
                <option value="ft">ft</option>
                <option value="in">in</option>
              </select>
            </div>
          )}

          {/* Arrow size */}
          {markup.type === 'arrow' && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>Arrow</span>
              <input
                type="range" min="4" max="30" step="1"
                value={localArrowSize}
                className={styles.slider}
                onChange={(e) => {
                  const v = +e.target.value;
                  setLocalArrowSize(v);
                  onUpdated({ ...markup, data: { ...markup.data, arrowSize: v } });
                }}
                onMouseUp={(e) => patch({ data: { ...markup.data, arrowSize: +e.target.value } })}
              />
              <span className={styles.sliderVal}>{localArrowSize}px</span>
            </div>
          )}

          {/* Font size */}
          {markup.type === 'text' && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>Size</span>
              <input
                type="range" min="8" max="72" step="1"
                value={localFontSize}
                className={styles.slider}
                onChange={(e) => {
                  const v = +e.target.value;
                  setLocalFontSize(v);
                  onUpdated({ ...markup, data: { ...markup.data, fontSize: v } });
                }}
                onMouseUp={(e) => patch({ data: { ...markup.data, fontSize: +e.target.value } })}
              />
              <span className={styles.sliderVal}>{localFontSize}pt</span>
            </div>
          )}

          {/* Linked file — pick one file from /Files to attach to this
              markup. Hover preview + click-to-open is rendered on the
              canvas; this section just controls the link. */}
          <div className={styles.linkRow}>
            <div className={styles.linkLabel}>LINKED FILE</div>
            {markup.linked_file ? (
              <div className={styles.linkCurrent}>
                <span className={styles.linkName} title={markup.linked_file.name}>
                  {markup.linked_file.name}
                </span>
                <div className={styles.linkBtns}>
                  <button
                    className={styles.linkBtn}
                    onClick={() => setPickingFile(true)}
                    disabled={saving}
                  >Change</button>
                  <button
                    className={styles.linkBtnRemove}
                    onClick={() => patch({ linked_file_id: null })}
                    disabled={saving}
                  >Remove</button>
                </div>
              </div>
            ) : (
              <button
                className={styles.linkPickBtn}
                onClick={() => setPickingFile(true)}
                disabled={saving}
              >
                <LinkIcon /> Link a file…
              </button>
            )}
          </div>

          {/* Linked sheet — independent of linked_file; same UX (Pick /
              Change / Remove). The picker excludes the markup's parent
              sheet so users can't link a markup to its own sheet. */}
          <div className={styles.linkRow}>
            <div className={styles.linkLabel}>LINKED SHEET</div>
            {markup.linked_sheet ? (
              <div className={styles.linkCurrent}>
                <span
                  className={styles.linkName}
                  title={`${markup.linked_sheet.sheet_number}${markup.linked_sheet.title ? ' — ' + markup.linked_sheet.title : ''}`}
                >
                  {markup.linked_sheet.sheet_number}
                </span>
                <div className={styles.linkBtns}>
                  <button
                    className={styles.linkBtn}
                    onClick={() => setPickingSheet(true)}
                    disabled={saving}
                  >Change</button>
                  <button
                    className={styles.linkBtnRemove}
                    onClick={() => patch({ linked_sheet_id: null })}
                    disabled={saving}
                  >Remove</button>
                </div>
              </div>
            ) : (
              <button
                className={styles.linkPickBtn}
                onClick={() => setPickingSheet(true)}
                disabled={saving}
              >
                <LinkIcon /> Link a sheet…
              </button>
            )}
          </div>

          {/* Note */}
          <div className={styles.noteRow}>
            <textarea
              className={styles.noteInput}
              placeholder="Add note…"
              value={note}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => { if (note !== (markup.note || '')) patch({ note }); }}
            />
          </div>
        </>
      )}

      {!canEdit && markup.linked_file && (
        <div className={styles.linkReadOnly}>
          <LinkIcon /> {markup.linked_file.name}
        </div>
      )}

      {!canEdit && markup.linked_sheet && (
        <div className={styles.linkReadOnly}>
          <LinkIcon /> {markup.linked_sheet.sheet_number}
        </div>
      )}

      {pickingFile && (
        <FilePickerModal
          title="Link a file to this markup"
          onClose={() => setPickingFile(false)}
          onSelect={(file) => {
            patch({ linked_file_id: file.id });
            setPickingFile(false);
          }}
        />
      )}

      {pickingSheet && (
        <SheetPickerModal
          title="Link a sheet to this markup"
          excludeId={markup.sheet_id}
          onClose={() => setPickingSheet(false)}
          onSelect={(sheet) => {
            patch({ linked_sheet_id: sheet.id });
            setPickingSheet(false);
          }}
        />
      )}

      {!canEdit && markup.note && (
        <div className={styles.noteReadOnly}>{markup.note}</div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        {canPublish && (
          <button
            className={markup.published ? styles.unpublishBtn : styles.publishBtn}
            onClick={() => patch({ published: !markup.published })}
            disabled={saving}
          >
            {saving ? '…' : markup.published ? 'Unpublish' : 'Publish'}
          </button>
        )}
        {canDelete && (
          <button className={styles.deleteBtn} onClick={handleDelete} disabled={saving}>
            {saving ? '…' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}

function getMarkupBBox(markup) {
  const d = markup.data || {};
  switch (markup.type) {
    case 'arrow': case 'line': case 'measure':
      return {
        x: Math.min(d.x1||0, d.x2||0),
        y: Math.min(d.y1||0, d.y2||0),
        w: Math.abs((d.x2||0) - (d.x1||0)),
        h: Math.abs((d.y2||0) - (d.y1||0)),
      };
    case 'rect': case 'cloud': {
      const x = (d.w||0) < 0 ? (d.x||0) + (d.w||0) : (d.x||0);
      const y = (d.h||0) < 0 ? (d.y||0) + (d.h||0) : (d.y||0);
      return { x, y, w: Math.abs(d.w||0), h: Math.abs(d.h||0) };
    }
    case 'text':
      return { x: d.x||0, y: (d.y||0) - 16, w: ((d.text||'').length * 9 + 8), h: 22 };
    case 'freehand': {
      const pts = d.points || [];
      if (!pts.length) return { x: 0, y: 0, w: 20, h: 20 };
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs)-Math.min(...xs)||20, h: Math.max(...ys)-Math.min(...ys)||20 };
    }
    case 'measure-length':
    case 'measure-area':
    case 'measure-volume': {
      // line kind uses x1/y1/x2/y2; all others carry a points array.
      if (d.kind === 'line') {
        return {
          x: Math.min(d.x1||0, d.x2||0),
          y: Math.min(d.y1||0, d.y2||0),
          w: Math.abs((d.x2||0) - (d.x1||0)),
          h: Math.abs((d.y2||0) - (d.y1||0)),
        };
      }
      if (d.kind === 'rect') {
        const x = (d.w||0) < 0 ? (d.x||0) + (d.w||0) : (d.x||0);
        const y = (d.h||0) < 0 ? (d.y||0) + (d.h||0) : (d.y||0);
        return { x, y, w: Math.abs(d.w||0), h: Math.abs(d.h||0) };
      }
      const pts = d.points || [];
      if (!pts.length) return { x: 0, y: 0, w: 20, h: 20 };
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        w: Math.max(...xs) - Math.min(...xs) || 20,
        h: Math.max(...ys) - Math.min(...ys) || 20,
      };
    }
    default: return { x: 0, y: 0, w: 20, h: 20 };
  }
}

const LinkIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path d="M7 9l-2.5 2.5a2.5 2.5 0 11-3.5-3.5L3.5 5.5"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 7l2.5-2.5a2.5 2.5 0 113.5 3.5L12.5 10.5"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M5.5 10.5l5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
